import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Circle,
  Image,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useCreateActivityMutation } from "@store/api/activities";
import { useToastNotification } from "@hooks";
import { activityTitles } from "@constants/activities";
import blockGameIcon from "../../assets/svg/block-game-icon.svg";
import NoteCreationModal from "./NoteCreationModal";
import PollCreationModal from "./PollCreationModal";

const ActivitySelectionModal = ({ isOpen, onClose, onAddActivity }) => {
  const { agendaId } = useParams();
  const [activeModal, setActiveModal] = useState(null); // 'note', 'poll', or null
  const [selectedActivityType, setSelectedActivityType] = useState(null);
  const [createActivity] = useCreateActivityMutation();
  const { showSuccessToast, showErrorToast } = useToastNotification();
  const activityOptions = [
    {
      id: "poll",
      emoji: "📈",
      title: "Get feedback from audience",
      subtitle: "Poll or Survey",
      bgColor: "yellow.400", // #FDB022
    },
    {
      id: "quiz",
      emoji: "📝",
      title: "Test to see if content stuck",
      subtitle: "Knowledge Check",
      bgColor: "blue.500", // #35C0FC
    },
    {
      id: "link",
      emoji: "📚",
      title: "Share a link for reference or action",
      subtitle: "Link Share",
      bgColor: "green.400", // #32D583
    },
    {
      id: "note",
      emoji: "💡",
      title: "Drop a one way message to your crowd",
      subtitle: "Trainer's Tip",
      bgColor: "#4C47CC", // Exact Figma design purple (not in theme)
    },
  ];

  const handleActivitySelect = (activityType) => {
    // Open the appropriate modal based on activity type
    if (activityType === "poll" || activityType === "quiz") {
      setActiveModal("poll");
      setSelectedActivityType(activityType || "poll");
    } else if (activityType === "link" || activityType === "note") {
      setActiveModal("note");
      setSelectedActivityType(activityType || "note");
    } else {
      // Default behavior for the generic "Add Activity" button - go to poll creation
      setActiveModal("poll");
      setSelectedActivityType("poll");
    }
  };

  const handleModalSave = async (values) => {
    try {
      // Map frontend activity types to backend types
      const backendActivityType =
        selectedActivityType === "link" || selectedActivityType === "note"
          ? "share"
          : selectedActivityType;

      // Transform the payload based on activity type
      let activityBody;

      if (selectedActivityType === "link") {
        // Transform link activity to share activity format
        activityBody = {
          agendaId,
          type: "share",
          displayName: values.displayName,
          title: values.title,
          content: values.options?.message || "",
          link: values.options?.link || null,
          settings: {
            persistent: true,
            blast: true,
            ...(values.settings?.timing && { timing: values.settings.timing }),
          },
        };
      } else if (selectedActivityType === "note") {
        // Transform note activity to share activity format (no link)
        activityBody = {
          agendaId,
          type: "share",
          displayName: values.displayName,
          title: values.title,
          content: values.options?.message || "",
          link: null,
          settings: {
            persistent: true,
            blast: true,
            ...(values.settings?.timing && { timing: values.settings.timing }),
          },
        };
      } else {
        // For poll/quiz activities, use the original format
        activityBody = {
          agendaId,
          type: backendActivityType,
          displayName: values.displayName,
          title: values.title,
          options: values.options,
          settings: values.settings,
        };
      }

      // Call the API to create the activity
      const result = await createActivity(activityBody).unwrap();

      // Show success notification
      const activityTitle = activityTitles[selectedActivityType] || "Activity";
      showSuccessToast(`${activityTitle} created successfully`);

      // Call the original callback for backwards compatibility
      onAddActivity?.(selectedActivityType, values);

      // Close all modals
      setActiveModal(null);
      setSelectedActivityType(null);
      onClose();

      // Return the result so the modal can access the generated display name
      return result;
    } catch (error) {
      // Show error notification
      const activityTitle = activityTitles[selectedActivityType] || "Activity";
      showErrorToast(error.data?.message || `Error creating ${activityTitle}`);
      console.error("Error creating activity:", error);
    }
  };

  const handleModalClose = () => {
    setActiveModal(null);
    setSelectedActivityType(null);
  };

  const handleNevermind = () => {
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          bg="white"
          borderRadius="32px 32px 0px 0px"
          maxW="425px"
          mx={4}
          mb={0}
          position="relative"
          top="auto"
          bottom={0}
          transform="none"
        >
          {/* Background gradient rectangle */}
          <Box
            position="absolute"
            top="0.72px"
            left={0}
            width="100%"
            height="126.28px"
            bgGradient="linear(180deg, rgba(237, 237, 250, 1) 0%, rgba(255, 255, 255, 1) 100%)"
            zIndex={-1}
            borderRadius="32px 32px 0px 0px"
          />

          <ModalBody p={0}>
            <VStack spacing={8} p="32px 16px 16px" align="center">
              {/* Header Section */}
              <VStack spacing={3} align="stretch" width="100%">
                {/* Title with Icon */}
                <HStack spacing={2} align="center">
                  <Image src={blockGameIcon} alt="Add Activity" boxSize={6} />
                  <Text
                    fontSize="2xl"
                    fontWeight="semibold"
                    color="black"
                    fontFamily="Geist, sans-serif"
                    letterSpacing="-0.04em"
                    lineHeight="1.33"
                  >
                    Add Activity
                  </Text>
                </HStack>

                {/* Subtitle */}
                <Text
                  fontSize="sm"
                  fontWeight="normal"
                  color="granite"
                  fontFamily="Geist, sans-serif"
                  letterSpacing="-0.02em"
                  lineHeight="1.43"
                  width="100%"
                >
                  Add any type of Poll, Quiz, Link or Note on the fly
                </Text>
              </VStack>

              {/* Activity Options */}
              <VStack spacing={3} align="stretch" width="100%">
                {activityOptions.map((option) => (
                  <Box
                    key={option.id}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                    p={3}
                    cursor="pointer"
                    boxShadow="0px 2px 4px 0px rgba(12, 0, 28, 0.04), 0px 0px 6px 0px rgba(12, 0, 28, 0.02)"
                    transition="all 0.2s"
                    _hover={{
                      transform: "translateY(-1px)",
                      boxShadow:
                        "0px 4px 8px 0px rgba(12, 0, 28, 0.08), 0px 0px 12px 0px rgba(12, 0, 28, 0.04)",
                    }}
                    _active={{
                      transform: "scale(0.98)",
                    }}
                    onClick={() => handleActivitySelect(option.id)}
                    data-testid={`activity-type-${option.id}`}
                  >
                    <HStack spacing={3} align="center">
                      {/* Emoji Icon with Background */}
                      <Circle
                        size={8}
                        bg={option.bgColor}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text
                          fontSize="md"
                          lineHeight="1.5"
                          letterSpacing="-0.02em"
                          fontFamily="Geist, sans-serif"
                        >
                          {option.emoji}
                        </Text>
                      </Circle>

                      {/* Text Content */}
                      <VStack spacing={1} align="stretch" flex={1}>
                        <Text
                          fontSize="xs"
                          fontWeight="normal"
                          color="black"
                          fontFamily="Geist, sans-serif"
                          letterSpacing="-0.01em"
                          lineHeight="1.5"
                          noOfLines={1}
                        >
                          {option.title}
                        </Text>
                        <Text
                          fontSize="11px"
                          fontWeight="normal"
                          color="granite"
                          fontFamily="Geist, sans-serif"
                          letterSpacing="-0.01em"
                          lineHeight="1.45"
                          noOfLines={1}
                        >
                          {option.subtitle}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>

              {/* Footer Section */}
              <VStack spacing={4} align="stretch" width="100%">
                {/* Add Activity Button */}
                <Button
                  bg="#4C47CC"
                  color="white"
                  fontSize="sm"
                  fontWeight="semibold"
                  fontFamily="Geist, sans-serif"
                  borderRadius="md"
                  width="100%"
                  py={4}
                  px={14}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1.5}
                  _hover={{ bg: "#3D39A3" }}
                  _active={{ bg: "#3D39A3", transform: "scale(0.98)" }}
                  onClick={() => handleActivitySelect()}
                >
                  ✨ Add Activity
                </Button>

                {/* Nevermind Link */}
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="black"
                  fontFamily="Geist, sans-serif"
                  textAlign="center"
                  cursor="pointer"
                  lineHeight="1.23"
                  _hover={{
                    textDecoration: "underline",
                    color: "granite",
                  }}
                  onClick={handleNevermind}
                >
                  Nevermind
                </Text>
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Note Creation Modal */}
      <NoteCreationModal
        isOpen={activeModal === "note"}
        onClose={handleModalClose}
        activityType={selectedActivityType}
        onSave={handleModalSave}
      />

      {/* Poll Creation Modal */}
      <PollCreationModal
        isOpen={activeModal === "poll"}
        onClose={handleModalClose}
        activityType={selectedActivityType}
        onSave={handleModalSave}
      />
    </>
  );
};

export default ActivitySelectionModal;
